import { useCallback, useRef, useState } from 'react';

import type { MapHoverHandler } from '../types';
import { useDebounce } from './useDebounce';

const TOOLTIP_HIDE_DELAY_MS = 120;

type HoverResult = {
  feature: TooltipFeature;
  coords: [number, number] | null;
  changed: boolean;
};

type TooltipFeature = {
  id: number;
  type: number;
  label?: unknown;
  location?: unknown;
} | null;

function featureKey(f: TooltipFeature): string | null {
  if (!f) return null;
  return `${f.id}:${f.type}`;
}

export function useHoveredFeature(options?: {
  buildFeature: (
    props: Record<string, unknown>,
    layerId: string
  ) => TooltipFeature | Record<string, unknown> | null;
  throttleMs?: number;
}): {
  hover: HoverResult;
  report: MapHoverHandler;
} {
  const { buildFeature, throttleMs = 50 } = options || {};
  const lastKeyRef = useRef<string | null>(null);
  const pendingLeaveRef = useRef<number | null>(null);
  const [hover, setHover] = useState<HoverResult>({
    feature: null,
    coords: null,
    changed: false,
  });

  const clearLeaveTimeout = useCallback(() => {
    if (pendingLeaveRef.current != null) {
      window.clearTimeout(pendingLeaveRef.current);
      pendingLeaveRef.current = null;
    }
  }, []);

  const { run: setHoverThrottled } = useDebounce((next: HoverResult) => {
    setHover((prev) => {
      const prevKey = featureKey(prev.feature);
      const nextKey = featureKey(next.feature);
      const changed = prevKey !== nextKey;
      if (changed) return { ...next, changed: true };
      return { ...next, changed: false };
    });
  }, throttleMs);

  const report = useCallback<MapHoverHandler>(
    (props, layerId, lngLat) => {
      if (!props || !layerId || !lngLat) {
        if (!pendingLeaveRef.current) {
          pendingLeaveRef.current = window.setTimeout(() => {
            lastKeyRef.current = null;
            setHover({ feature: null, coords: null, changed: true });
            pendingLeaveRef.current = null;
          }, TOOLTIP_HIDE_DELAY_MS);
        }
        return;
      }

      clearLeaveTimeout();

      const rawBuilt = buildFeature?.(props, layerId);
      const enriched = (() => {
        if (!rawBuilt) return null;
        const rb = rawBuilt as {
          id?: unknown;
          type?: unknown;
          label?: unknown;
          location?: unknown;
        };
        if (typeof rb.id !== 'undefined' && typeof rb.type !== 'undefined') {
          const idNum = Number(rb.id as number | string);
          const typeNum = Number(rb.type as number | string);
          if (!Number.isNaN(idNum) && !Number.isNaN(typeNum)) {
            return {
              id: idNum,
              type: typeNum,
              label: rb.label,
              location: rb.location,
            } as TooltipFeature;
          }
        }
        return null;
      })();

      if (!enriched) {
        lastKeyRef.current = null;
        setHover({ feature: null, coords: null, changed: true });
        return;
      }

      const key = featureKey(enriched);
      setHoverThrottled({
        feature: enriched,
        coords: lngLat,
        changed: lastKeyRef.current !== key,
      });
      lastKeyRef.current = key;
    },
    [buildFeature, setHoverThrottled, clearLeaveTimeout]
  );

  return { hover, report } as const;
}
